import {
    AlertTriangle,
    ChevronDown,
    ChevronLeft,
    Info,
    LoaderCircle,
    Save,
    Sparkles,
    ToggleLeft,
    ToggleRight,
} from 'lucide-react'
import React, { useContext, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './ReviseComponentScreen.module.css'

const VALUE_TYPE_OPTIONS = [
    { value: 'FLAT', label: 'Fixed Amount (₹)', hint: 'Fixed monthly amount' },
    { value: 'PERCENT_OF_BASIC', label: '% of Basic Pay', hint: 'Depends on active BASIC component' },
    { value: 'PERCENT_OF_GROSS', label: '% of Total Gross', hint: 'Calculated on gross salary' },
]

const parseActiveStore = (raw) => {
    try {
        return raw && raw.trim() !== '' ? JSON.parse(raw) : null
    } catch {
        return null
    }
}

const getFieldLabel = (field) => {
    const fieldMap = {
        componentName: 'Name',
        value: 'Amount',
        valueType: 'Calculation method',
        isTaxable: 'Taxability',
        sortOrder: 'Position',
    }
    return fieldMap[field] || field
}

const ReviseComponentScreen = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { templateIdentifier } = useParams()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const component = location.state?.component
    const routeTemplateName = location.state?.templateName || 'Salary Template'

    const [formData, setFormData] = useState(() => ({
        componentName: component?.componentName || '',
        valueType: component?.valueType || '',
        value: component?.value !== undefined && component?.value !== null ? String(component.value) : '',
        isTaxable: component?.isTaxable === 1,
        sortOrder: component?.sortOrder !== undefined && component?.sortOrder !== null ? String(component.sortOrder) : '',
    }))

    const originalData = useMemo(
        () => ({
            componentName: component?.componentName || '',
            valueType: component?.valueType || '',
            value: Number(component?.value || 0),
            isTaxable: component?.isTaxable ?? 0,
            sortOrder: Number(component?.sortOrder || 0),
        }),
        [component]
    )

    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)
    const [vtPickerOpen, setVtPickerOpen] = useState(false)
    const vtPickerRef = useRef(null)

    const isStatutory = component?.isStatutory === 1
    const selectedVT = VALUE_TYPE_OPTIONS.find((opt) => opt.value === formData.valueType)
    const valueLabel = ['PERCENT_OF_BASIC', 'PERCENT_OF_GROSS'].includes(formData.valueType)
        ? 'Percentage (%) *'
        : 'Amount (₹) *'

    if (!component || !templateIdentifier) {
        return (
            <div className={styles.page}>
                <section className={styles.errorState}>
                    <AlertTriangle size={18} />
                    <p>Component details are missing. Please open this screen from Salary Structure detail.</p>
                    <button type="button" className={styles.backActionBtn} onClick={() => navigate(-1)}>
                        <ChevronLeft size={14} /> Back
                    </button>
                </section>
            </div>
        )
    }

    const hasChanges = () => {
        if (formData.componentName.trim() !== originalData.componentName) return true
        const sortValue = formData.sortOrder === '' ? originalData.sortOrder : Number(formData.sortOrder)
        if (sortValue !== originalData.sortOrder) return true
        if (!isStatutory) {
            if (formData.valueType !== originalData.valueType) return true
            if (Math.abs(Number(formData.value || 0) - originalData.value) > 0.001) return true
            if ((formData.isTaxable ? 1 : 0) !== originalData.isTaxable) return true
        }
        return false
    }

    const validate = () => {
        const newErrors = {}

        if (!formData.componentName.trim()) {
            newErrors.componentName = 'Component name cannot be empty.'
        } else if (formData.componentName.trim().length > 200) {
            newErrors.componentName = 'Must not exceed 200 characters.'
        }

        if (formData.sortOrder !== '' && Number(formData.sortOrder) < 0) {
            newErrors.sortOrder = 'Sort order must be 0 or greater.'
        }

        if (!isStatutory) {
            if (!formData.valueType) {
                newErrors.valueType = 'Please select a calculation method.'
            }
            if (formData.value === '') {
                newErrors.value = 'Value is required.'
            } else if (Number(formData.value) < 0) {
                newErrors.value = 'Value must be 0 or greater.'
            } else if (
                ['PERCENT_OF_BASIC', 'PERCENT_OF_GROSS'].includes(formData.valueType) &&
                Number(formData.value) > 100
            ) {
                newErrors.value = 'Percentage must be between 0 and 100.'
            }
        }

        return newErrors
    }

    const handleChange = (field, value) => {
        setFormData((prev) => ({ ...prev, [field]: value }))
        if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }))
    }

    const reviseComponent = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        const newErrors = validate()
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }

        if (!hasChanges()) {
            toast.info('No changes to save.')
            return
        }

        setErrors({})

        try {
            setIsSaving(true)

            const payload = {
                token: authCtx.token,
                storeIdentifier: activeStore.storeIdentifier,
                templateIdentifier,
                componentIdentifier: component.componentIdentifier,
            }

            const nextName = formData.componentName.trim()
            if (nextName !== originalData.componentName) {
                payload.componentName = nextName
            }

            if (formData.sortOrder !== '' && Number(formData.sortOrder) !== originalData.sortOrder) {
                payload.sortOrder = Number(formData.sortOrder)
            }

            if (!isStatutory) {
                if (formData.valueType !== originalData.valueType) {
                    payload.valueType = formData.valueType
                }
                if (Math.abs(Number(formData.value || 0) - originalData.value) > 0.001) {
                    payload.value = Number(formData.value)
                }
                if ((formData.isTaxable ? 1 : 0) !== originalData.isTaxable) {
                    payload.isTaxable = formData.isTaxable ? 1 : 0
                }
            }

            const response = await fetch(BASE_URL_API + 'reviseSalaryStructureComponent', {
                method: 'POST',
                body: JSON.stringify(payload),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                const count = respo?.response?.changeCount ?? 1
                const baseName = component.componentName || 'Component'
                toast.success(`${baseName} updated — ${count} change${count !== 1 ? 's' : ''} saved.`)

                const changes = Array.isArray(respo?.response?.changes) ? respo.response.changes : []
                if (changes.length > 0) {
                    const summary = changes.map((c) => getFieldLabel(c.field)).join(', ')
                    toast.success(`Updated: ${summary}`)
                }

                navigate(-1)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            switch (respo?.errorCode) {
                case 'VAL_001':
                    setErrors((prev) => ({ ...prev, componentName: respo?.message }))
                    break
                case 'VAL_002':
                    if (respo?.message?.includes('sortOrder')) {
                        setErrors((prev) => ({ ...prev, sortOrder: respo?.message }))
                    } else {
                        setErrors((prev) => ({ ...prev, value: respo?.message }))
                    }
                    break
                case 'VAL_003':
                    setErrors((prev) => ({ ...prev, valueType: respo?.message }))
                    break
                case 'VAL_007':
                    toast.error('Component no longer exists. It may have been removed.')
                    navigate(-1)
                    break
                case 'VAL_008':
                    toast.error(respo?.message || 'Unable to save this revision.')
                    if (respo?.message?.toLowerCase().includes('archived')) navigate(-1)
                    break
                case 'COMP_002':
                    setErrors((prev) => ({ ...prev, valueType: 'Add a BASIC component first.' }))
                    break
                case 'COMP_004':
                    toast.error(respo?.message || 'Template not found.')
                    navigate(-1)
                    break
                default:
                    toast.error(respo?.message || 'Failed to save revision.')
                    break
            }
        } catch (error) {
            console.error('reviseSalaryStructureComponent error:', error)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    const valueChanged = !isStatutory && (
        formData.valueType !== originalData.valueType ||
        Math.abs(Number(formData.value || 0) - originalData.value) > 0.001
    )

    return (
        <div className={styles.page}>
            <header className={styles.header}>
                <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
                    <ChevronLeft size={20} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}><Sparkles size={12} /> Edit Component</span>
                    <h1 className={styles.headerTitle}>{component.componentName}</h1>
                    <p className={styles.headerSubTitle}>{routeTemplateName}</p>
                </div>
            </header>

            <div className={styles.formWrap}>
                <section className={styles.infoBanner}>
                    <Info size={14} />
                    <p>Editing this component saves a revision history. Previous values remain visible in audit trail.</p>
                </section>

                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Display Name <span className={styles.required}>*</span></label>
                    <input
                        className={`${styles.textInput} ${errors.componentName ? styles.textInputError : ''}`}
                        type="text"
                        maxLength={200}
                        value={formData.componentName}
                        onChange={(e) => handleChange('componentName', e.target.value)}
                        placeholder="Component display name"
                    />
                    {errors.componentName && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.componentName}</p>}
                </div>

                <div className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>Payslip Position</label>
                    <input
                        className={`${styles.textInput} ${errors.sortOrder ? styles.textInputError : ''}`}
                        type="number"
                        inputMode="numeric"
                        min="0"
                        value={formData.sortOrder}
                        onChange={(e) => handleChange('sortOrder', e.target.value)}
                        placeholder="e.g. 10"
                    />
                    <p className={styles.fieldHint}>Lower number = appears earlier on payslip.</p>
                    {errors.sortOrder && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.sortOrder}</p>}
                </div>

                {isStatutory ? (
                    <section className={styles.statutoryCard}>
                        <h4>This is a statutory component.</h4>
                        <p>
                            Value, value type, and taxability are auto-computed by the payroll engine.
                            Only display name and payslip position can be customised.
                        </p>
                        <div className={styles.readOnlyRow}>
                            <span>Current value</span>
                            <strong>{component.valueDisplay || 'Auto-computed'}</strong>
                        </div>
                    </section>
                ) : (
                    <>
                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>Calculation Method <span className={styles.required}>*</span></label>
                            <div className={styles.pickerWrap} ref={vtPickerRef}>
                                <button
                                    type="button"
                                    className={`${styles.pickerTrigger} ${errors.valueType ? styles.textInputError : ''}`}
                                    onClick={() => setVtPickerOpen((p) => !p)}
                                >
                                    {selectedVT ? (
                                        <span className={styles.vtSelected}>
                                            <span>{selectedVT.label}</span>
                                            <small>{selectedVT.hint}</small>
                                        </span>
                                    ) : (
                                        <span className={styles.placeholder}>Select calculation method</span>
                                    )}
                                    <ChevronDown size={15} className={`${styles.chevron} ${vtPickerOpen ? styles.chevronOpen : ''}`} />
                                </button>

                                {vtPickerOpen && (
                                    <div className={styles.pickerDropdown}>
                                        {VALUE_TYPE_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                className={`${styles.pickerOption} ${formData.valueType === opt.value ? styles.pickerOptionActive : ''}`}
                                                onClick={() => {
                                                    handleChange('valueType', opt.value)
                                                    setVtPickerOpen(false)
                                                }}
                                            >
                                                <span>{opt.label}</span>
                                                <small>{opt.hint}</small>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {errors.valueType && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.valueType}</p>}
                        </div>

                        <div className={styles.fieldGroup}>
                            <label className={styles.fieldLabel}>{valueLabel}</label>
                            <div className={styles.valueInputWrap}>
                                <span className={styles.valuePrefix}>{['PERCENT_OF_BASIC', 'PERCENT_OF_GROSS'].includes(formData.valueType) ? '%' : '₹'}</span>
                                <input
                                    className={`${styles.valueInput} ${errors.value ? styles.textInputError : ''}`}
                                    type="number"
                                    inputMode="decimal"
                                    min="0"
                                    max={['PERCENT_OF_BASIC', 'PERCENT_OF_GROSS'].includes(formData.valueType) ? '100' : undefined}
                                    value={formData.value}
                                    onChange={(e) => handleChange('value', e.target.value)}
                                    placeholder={formData.valueType === 'FLAT' ? '16000' : '40'}
                                />
                            </div>
                            {errors.value && <p className={styles.fieldError}><AlertTriangle size={12} /> {errors.value}</p>}
                        </div>

                        <div className={styles.toggleRow}>
                            <div>
                                <p className={styles.toggleLabel}>Taxable Earning</p>
                                <p className={styles.toggleHint}>
                                    {formData.isTaxable
                                        ? 'Included in gross for TDS computation.'
                                        : 'Tax-exempt under your configured policy.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                className={styles.toggleBtn}
                                onClick={() => handleChange('isTaxable', !formData.isTaxable)}
                            >
                                {formData.isTaxable ? <ToggleRight size={32} className={styles.toggleOn} /> : <ToggleLeft size={32} className={styles.toggleOff} />}
                            </button>
                        </div>
                    </>
                )}

                {valueChanged && (
                    <section className={styles.previousCard}>
                        <p className={styles.previousLabel}>Previous</p>
                        <p className={styles.previousValue}>{component.valueDisplay || '—'}</p>
                    </section>
                )}

                <button
                    type="button"
                    className={`${styles.saveBtn} ${(!hasChanges() || isSaving) ? styles.saveBtnDisabled : ''}`}
                    disabled={!hasChanges() || isSaving}
                    onClick={reviseComponent}
                >
                    {isSaving
                        ? <><LoaderCircle size={16} className={styles.spin} /> Saving...</>
                        : <><Save size={16} /> Save Changes</>}
                </button>
            </div>
        </div>
    )
}

export default ReviseComponentScreen